import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  CORRECTION_TURNAROUND_HOURS,
  CorrectionsViewKind,
  PendingCorrection,
  oldestPending,
} from 'utils/correctionsState'
import { formatCorrectionDayMonth } from './correctionDates'

interface Props {
  kind: Exclude<CorrectionsViewKind, 'has-corrections'>
  pending: PendingCorrection[]
  /** Both CTAs lead to the story feed: writing is the only way to feed this page. */
  onGoToStories: () => void
  /** Opens support, for a wait that has run past what we promised. */
  onContactSupport: () => void
  /** Where a learner whose plan no longer includes corrections goes to upgrade. */
  onUpgrade: () => void
}

const submittedOn = (correction: PendingCorrection | null): string | null =>
  typeof correction?.createdAt === 'number' ? formatCorrectionDayMonth(correction.createdAt) : null

/**
 * What the corrections page shows instead of a list. Each variant names the ONE
 * action that moves the learner forward: send work in, or wait for the teacher.
 * "No corrections found" said neither, which is why a learner who had just
 * submitted read it as their work having vanished.
 */
const CorrectionsEmptyState: React.FC<Props> = ({ kind, pending, onGoToStories, onContactSupport, onUpgrade }) => {
  const { t } = useTranslation('corrections')
  const hours = CORRECTION_TURNAROUND_HOURS
  const waitingSince = submittedOn(oldestPending(pending))

  // A downgraded Pro with no correction history. Pointing them at the writing
  // step would promise a teacher their plan no longer pays for.
  if (kind === 'not-entitled') {
    return (
      <div className='corrections-empty'>
        <i className='fa fa-lock corrections-empty-icon' aria-hidden='true' />
        <h2 className='corrections-empty-title'>{t('emptyState.notEntitled.title')}</h2>
        <p className='corrections-empty-body'>{t('emptyState.notEntitled.body', { hours })}</p>
        <div className='corrections-empty-actions'>
          <button className='button-2 w-button green-btn-class' onClick={onUpgrade}>
            {t('emptyState.notEntitled.upgrade')}
          </button>
          <button className='corrections-empty-link' onClick={onGoToStories}>
            {t('emptyState.notEntitled.back')}
          </button>
        </div>
      </div>
    )
  }

  if (kind === 'awaiting' || kind === 'awaiting-overdue') {
    const overdue = kind === 'awaiting-overdue'
    return (
      <div className='corrections-empty'>
        <i className='fa fa-paper-plane corrections-empty-icon' aria-hidden='true' />
        <h2 className='corrections-empty-title'>{t('emptyState.awaiting.title')}</h2>
        <p className='corrections-empty-body'>
          {waitingSince
            ? t('emptyState.awaiting.receivedOn', { date: waitingSince })
            : t('emptyState.awaiting.received')}{' '}
          {overdue ? t('emptyState.awaiting.overdue') : t('emptyState.awaiting.onTime', { hours })}
        </p>
        <p className='corrections-empty-note'>{t('emptyState.awaiting.note')}</p>
        <div className='corrections-empty-actions'>
          <button className='button-2 w-button green-btn-class' onClick={onGoToStories}>
            {t('emptyState.awaiting.keepLearning')}
          </button>
          {overdue && (
            <button className='corrections-empty-link' onClick={onContactSupport}>
              {t('emptyState.awaiting.chase')}
            </button>
          )}
        </div>
      </div>
    )
  }

  // 'nothing-submitted' and 'unknown'. They share a shape: the learner has no
  // finished correction and the next step is to send work in. They differ in
  // whether we can say the queue is empty. On 'unknown' the pending request
  // failed, so we say what is true of both cases instead of guessing.
  const unknown = kind === 'unknown'
  return (
    <div className='corrections-empty'>
      <i className='fa fa-pencil corrections-empty-icon' aria-hidden='true' />
      <h2 className='corrections-empty-title'>{t('emptyState.none.title')}</h2>
      <p className='corrections-empty-body'>
        {unknown ? t('emptyState.none.unknownBody', { hours }) : t('emptyState.none.body', { hours })}
      </p>
      <div className='corrections-empty-actions'>
        <button className='button-2 w-button green-btn-class' onClick={onGoToStories}>
          {t('emptyState.none.start')}
        </button>
      </div>
    </div>
  )
}

export default CorrectionsEmptyState
